import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { AiFillStar } from "react-icons/ai";
import { useEffect, useRef } from "react";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import MarkerClusterGroup from "@changey/react-leaflet-markercluster";
import { useSearchContext } from "../contexts/SearchContext";

interface HotelProps {
  hotels: any[];
  hoveredHotelId: string | null;
}

const UpdateMapCenter = ({ center }: { center: [number, number] }) => {
  const map = useMap();
  map.setView(center); // Dynamically update the map's center
  return null;
};

const HotelMap = ({ hotels, hoveredHotelId }: HotelProps) => {
  const markerRefs = useRef<{ [key: string]: L.Marker }>({});
  const defaultCenter: [number, number] = [51.5074, -0.1278]; // Default center (London)
  const center: [number, number] =
    hotels.length > 0
      ? [hotels[0].latitude, hotels[0].longitude]
      : defaultCenter;
  const search = useSearchContext();

  // const flyToHotel = (hotel: { latitude: number; longitude: number }) => {
  //   if (map && hotel?.latitude && hotel?.longitude) {
  //     map.flyTo([hotel.latitude, hotel.longitude], 16, { duration: 0.5 });
  //   }
  // };

  // useEffect(() => {
  //   if (setFlyToHotelFn) setFlyToHotelFn(flyToHotel);
  // }, [setFlyToHotelFn, map]);

  useEffect(() => {
    if (hoveredHotelId && markerRefs.current[hoveredHotelId]) {
      markerRefs.current[hoveredHotelId].openPopup();
    } else {
      // Close all popups if nothing is hovered
      Object.values(markerRefs.current).forEach((marker) =>
        marker.closePopup()
      );
    }
  }, [hoveredHotelId]);

  // const FlyToHotelHelper = ({
  //   setFlyToHotelFn,
  // }: {
  //   setFlyToHotelFn?: (fn: (hotel: any) => void) => void;
  // }) => {
  //   const map = useMap();
  //   useEffect(() => {
  //     if (setFlyToHotelFn) {
  //       setFlyToHotelFn((hotel: { latitude: number; longitude: number }) => {
  //         if (hotel?.latitude && hotel?.longitude) {
  //           map.flyTo([hotel.latitude, hotel.longitude], 16, { duration: 0.5 });
  //         }
  //       });
  //     }
  //   }, [setFlyToHotelFn, map]);
  //   return null;
  // };

  return (
    <MapContainer
      center={defaultCenter}
      zoom={10}
      className="h-full w-full z-10 rounded-lg"
    >
      <TileLayer
        attribution="&copy; OpenStreetMap contributors"
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {/* Dynamically update the center */}
      <UpdateMapCenter center={center} />
      <MarkerClusterGroup
        chunkedLoading
        showCoverageOnHover={false}
        maxClusterRadius={50}
      >
        {hotels.map((hotel, index) => {
          // const totalAdults = search.guests.reduce((sum, g) => sum + g.adults, 0);
          // const totalChildren = search.guests.reduce((sum, g) => sum + g.children.length, 0);
          const searchParams = new URLSearchParams({
            checkIn: search.checkIn.toISOString(),
            checkOut: search.checkOut.toISOString(),
            guests: JSON.stringify(search.guests),
            roomCount: search.roomCount.toString(),
            nights: search.nights.toString(),
            vacationType: search.vacationType,
            hotelId: hotel.id,
            regionId: search.regionId,
          }).toString();
          return (
            <Marker
              key={`${hotel.id || hotel.name}-${index}`}
              position={[hotel.latitude, hotel.longitude]}
              icon={L.icon({
                iconUrl: "/navigator.png",
                iconSize: [25, 25],
                iconAnchor: [12, 40],
                popupAnchor: [0, -30],
              })}
              ref={(ref) => {
                if (ref) markerRefs.current[hotel.id] = ref;
              }}
              eventHandlers={{
                mouseover: (e) => e.target.openPopup(),
                mouseout: (e) => e.target.closePopup(),
                click: (e) => {
                  e.target.openPopup();
                  window.open(
                    `/resort/${hotel.id}?${searchParams}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                },
              }}
            >
              <Popup>
                <div style={{ display: "flex", width: "220px" }}>
                  <img
                    src={
                      hotel?.images?.[0]?.replace("{size}", "640x400") ??
                      "https://via.placeholder.com/100x70"
                    }
                    alt={hotel?.name || "Hotel Image"}
                    style={{
                      width: "80px",
                      height: "60px",
                      objectFit: "cover",
                      borderRadius: "6px",
                      marginRight: "10px",
                    }}
                  />

                  {/* Text Content */}
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    {/* Stars */}
                    <div style={{ display: "flex", marginBottom: "2px" }}>
                      {Array.from({ length: hotel?.star_rating || 0 }).map(
                        (_, i) => (
                          <AiFillStar key={i} color="#facc15" size={13} />
                        )
                      )}
                    </div>

                    {/* Hotel Name */}
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "13px",
                        lineHeight: "1.2",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "120px", // Limit width for the name
                      }}
                      title={hotel?.name} // Optional tooltip
                    >
                      {hotel?.name || "Unnamed Hotel"}
                    </div>

                    {/* Price */}
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#555",
                        marginTop: "4px",
                      }}
                    >
                      {hotel?.rates?.[0]?.payment_options?.[0]?.amount
                        ? `${((parseFloat(hotel.rates[0].payment_options[0].amount)) * 1.15).toFixed(2)} ${hotel.rates[0].payment_options[0].currency}`
                        : "Price N/A"}
                    </div>
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MarkerClusterGroup>
    </MapContainer>
  );
};

export default HotelMap;